import { useState, useEffect, useRef } from "react";
import { taxCalculatorApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Calculator, Save, Printer, Trash2, FileText, ChevronDown, ChevronUp,
  DollarSign, User, Building2, TrendingUp, Loader2, Plus, Eye, Edit2,
  Lightbulb, AlertCircle, ArrowRight
} from "lucide-react";

const FILING_STATUSES = [
  { value: "single", label: "Single" },
  { value: "married_joint", label: "Married Filing Jointly" },
  { value: "married_separate", label: "Married Filing Separately" },
  { value: "head_of_household", label: "Head of Household" },
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${value.toFixed(1)}%`;
};

// Currency input component
const CurrencyInput = ({ value, onChange, label, id, placeholder = "0" }) => {
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-sm text-muted-foreground">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
        <Input
          id={id}
          type="number"
          value={value || ""}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          placeholder={placeholder}
          className="pl-7"
        />
      </div>
    </div>
  );
};

// Collapsible section component
const CollapsibleSection = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-card hover:bg-muted transition-colors"
      >
        <div className="flex items-center gap-2 font-medium text-foreground">
          {Icon && <Icon size={18} className="text-blue-600" />}
          {title}
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="p-4 space-y-4">{children}</div>}
    </div>
  );
};

export default function TaxCalculator() {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [projections, setProjections] = useState([]);
  const [selectedProjection, setSelectedProjection] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [clientName, setClientName] = useState("");
  const [notes, setNotes] = useState("");
  const printRef = useRef();

  // Form state
  const [formData, setFormData] = useState({
    client_name: "",
    filing_status: "single",
    is_65_or_older: false,
    spouse_is_65_or_older: false,
    is_blind: false,
    spouse_is_blind: false,
    // Defaults to TRUE (covered or unknown — conservative) per IRS workflow.
    // Preparer must affirmatively un-check to claim full IRA deductibility.
    taxpayer_covered_by_retirement_plan: true,
    spouse_covered_by_retirement_plan: true,
    // HDHP enrollment gates HSA-eligibility planning cards.
    hdhp_enrolled: false,
    // Self-employed retirement (SEP/SIMPLE/Solo-401k); reduces QBI base.
    se_retirement_contribution: 0,
    income: {
      // Multiple W-2s with taxpayer/spouse designation
      w2_entries: [
        { label: "Taxpayer W-2", belongs_to: "taxpayer", gross_wages: 0, social_security_wages: 0, social_security_withheld: 0, federal_withholding: 0, covered_by_retirement_plan: false }
      ],
      // Multiple K-1s
      k1_entries: [],
      // Legacy fields for backwards compatibility (not shown in UI anymore)
      w2_primary_gross: 0,
      w2_secondary_gross: 0,
      w2_federal_withholding: 0,
      covered_by_retirement_plan: false,
      spouse_w2_primary_gross: 0,
      spouse_w2_secondary_gross: 0,
      spouse_w2_federal_withholding: 0,
      spouse_covered_by_retirement_plan: false,
      // Other income
      social_security_benefits: 0,
      interest_income: 0,
      tax_exempt_interest: 0,
      dividend_income: 0,
      qualified_dividends: 0,
      short_term_capital_gains: 0,
      long_term_capital_gains: 0,
      capital_loss_carryover: 0,
      pension_annuity_income: 0,
      rental_net_income: 0,
      farm_net_income: 0,
      farm_belongs_to: "taxpayer",
      farm_is_sstb: false,
      state_tax_refund: 0,
      other_income: 0,
      schedule_c_entries: [],
      // Legacy single Schedule C fields (kept for back-compat, hidden from UI)
      schedule_c_net_income: 0,
      schedule_c_belongs_to: "taxpayer",
      schedule_c_is_sstb: false,
      schedule_c_w2_wages: 0,
      // Legacy single K-1 fields
      k1_qbi_income: 0,
      k1_is_sstb: false,
      k1_subject_to_se_tax: false,
      k1_w2_wages: 0,
      k1_ubia: 0,
      reit_ptp_distributions: 0,
    },
    deductions: {
      mortgage_interest: 0,
      state_income_tax: 0,
      local_income_tax: 0,
      property_tax: 0,
      personal_property_tax: 0,
      sales_tax: 0,
      medical_expenses: 0,
      charitable_cash: 0,
      charitable_noncash: 0,
      health_insurance_premium: 0,
      student_loan_interest: 0,
      educator_expenses: 0,
      ira_contribution: 0,
      hsa_contribution: 0,
      qualified_overtime: 0,
      qualified_tips: 0,
    },
    credits: {
      num_children_under_17: 0,
      other_dependents: 0,
      estimated_tax_paid: 0,
      dependent_care_expenses: 0,
      num_dependents_for_care: 0,
      education_expenses: 0,
      num_students: 0,
      use_lifetime_learning: false,
      aca_advance_ptc: 0,
      aca_annual_benchmark_premium: 0,
    },
    use_itemized_deductions: null,
  });

  const [results, setResults] = useState(null);
  const [irmaaBrackets, setIrmaaBrackets] = useState([]);
  const [showReportDialog, setShowReportDialog] = useState(false);

  // Helper functions for W-2 and K-1 management
  const addW2Entry = () => {
    const isMarried = formData.filing_status === "married_joint";
    const existingCount = formData.income.w2_entries.length;
    const defaultBelongsTo = isMarried && existingCount === 1 ? "spouse" : "taxpayer";
    const label = isMarried 
      ? (defaultBelongsTo === "spouse" ? "Spouse W-2" : `W-2 #${existingCount + 1}`)
      : `W-2 #${existingCount + 1}`;
    
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        w2_entries: [
          ...prev.income.w2_entries,
          { label, belongs_to: defaultBelongsTo, gross_wages: 0, social_security_wages: 0, social_security_withheld: 0, federal_withholding: 0, covered_by_retirement_plan: false }
        ]
      }
    }));
  };

  const removeW2Entry = (index) => {
    if (formData.income.w2_entries.length <= 1) {
      toast.error("You must have at least one W-2 entry");
      return;
    }
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        w2_entries: prev.income.w2_entries.filter((_, i) => i !== index)
      }
    }));
  };

  const updateW2Entry = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        w2_entries: prev.income.w2_entries.map((entry, i) => 
          i === index ? { ...entry, [field]: value } : entry
        )
      }
    }));
  };

  const addK1Entry = () => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        k1_entries: [
          ...prev.income.k1_entries,
          { business_name: "", belongs_to: "taxpayer", qbi_income: 0, is_sstb: false, subject_to_se_tax: false, w2_wages: 0, ubia: 0 }
        ]
      }
    }));
  };

  const removeK1Entry = (index) => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        k1_entries: prev.income.k1_entries.filter((_, i) => i !== index)
      }
    }));
  };

  const updateK1Entry = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        k1_entries: prev.income.k1_entries.map((entry, i) => 
          i === index ? { ...entry, [field]: value } : entry
        )
      }
    }));
  };

  const addScheduleCEntry = () => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        schedule_c_entries: [
          ...(prev.income.schedule_c_entries || []),
          { business_name: "", belongs_to: "taxpayer", net_income: 0, is_sstb: false, w2_wages: 0 }
        ]
      }
    }));
  };

  const removeScheduleCEntry = (index) => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        schedule_c_entries: (prev.income.schedule_c_entries || []).filter((_, i) => i !== index)
      }
    }));
  };

  const updateScheduleCEntry = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      income: {
        ...prev.income,
        schedule_c_entries: (prev.income.schedule_c_entries || []).map((entry, i) =>
          i === index ? { ...entry, [field]: value } : entry
        )
      }
    }));
  };

  useEffect(() => {
    loadProjections();
  }, []);

  const loadProjections = async () => {
    try {
      setLoading(true);
      const res = await taxCalculatorApi.getProjections();
      setProjections(res.data);
    } catch (error) {
      console.error("Failed to load projections:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateFormField = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const calculateTax = async () => {
    try {
      setCalculating(true);
      const res = await taxCalculatorApi.calculate(formData);
      setResults(res.data);
      
      // Also fetch IRMAA brackets for the report
      try {
        const bracketsRes = await taxCalculatorApi.getIrmaaBrackets(formData.filing_status);
        setIrmaaBrackets(bracketsRes.data);
      } catch (e) {
        console.error("Failed to load IRMAA brackets:", e);
      }
      
      toast.success("Tax calculation complete");
    } catch (error) {
      toast.error("Failed to calculate tax");
      console.error(error);
    } finally {
      setCalculating(false);
    }
  };

  const saveProjection = async () => {
    if (!clientName.trim()) {
      toast.error("Please enter a client name");
      return;
    }

    try {
      setLoading(true);
      const data = {
        client_name: clientName,
        input_data: formData,
        notes: notes,
      };

      if (selectedProjection) {
        await taxCalculatorApi.updateProjection(selectedProjection.id, data);
        toast.success("Projection updated");
      } else {
        await taxCalculatorApi.createProjection(data);
        toast.success("Projection saved");
      }

      setShowSaveDialog(false);
      setClientName("");
      setNotes("");
      loadProjections();
    } catch (error) {
      toast.error("Failed to save projection");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjection = (projection) => {
    // Migrate old format to new format if needed
    const inputData = { ...projection.input_data };
    
    // Initialize w2_entries from legacy fields if not present
    if (!inputData.income?.w2_entries || inputData.income.w2_entries.length === 0) {
      const w2Entries = [];
      if (inputData.income?.w2_primary_gross > 0 || inputData.income?.w2_secondary_gross > 0) {
        w2Entries.push({
          label: "Taxpayer W-2",
          belongs_to: "taxpayer",
          gross_wages: (inputData.income?.w2_primary_gross || 0) + (inputData.income?.w2_secondary_gross || 0),
          social_security_wages: 0,
          social_security_withheld: 0,
          federal_withholding: inputData.income?.w2_federal_withholding || 0,
          covered_by_retirement_plan: inputData.income?.covered_by_retirement_plan || false
        });
      }
      if (inputData.income?.spouse_w2_primary_gross > 0 || inputData.income?.spouse_w2_secondary_gross > 0) {
        w2Entries.push({
          label: "Spouse W-2",
          belongs_to: "spouse",
          gross_wages: (inputData.income?.spouse_w2_primary_gross || 0) + (inputData.income?.spouse_w2_secondary_gross || 0),
          social_security_wages: 0,
          social_security_withheld: 0,
          federal_withholding: inputData.income?.spouse_w2_federal_withholding || 0,
          covered_by_retirement_plan: inputData.income?.spouse_covered_by_retirement_plan || false
        });
      }
      if (w2Entries.length === 0) {
        w2Entries.push({ label: "Taxpayer W-2", belongs_to: "taxpayer", gross_wages: 0, social_security_wages: 0, social_security_withheld: 0, federal_withholding: 0, covered_by_retirement_plan: false });
      }
      inputData.income = { ...inputData.income, w2_entries: w2Entries };
    } else {
      // Ensure existing w2_entries have the new fields
      inputData.income.w2_entries = inputData.income.w2_entries.map((entry, idx) => ({
        ...entry,
        belongs_to: entry.belongs_to || (idx === 0 ? "taxpayer" : "spouse"),
        social_security_wages: entry.social_security_wages || 0,
        social_security_withheld: entry.social_security_withheld || 0
      }));
    }
    
    // Initialize k1_entries from legacy field if not present
    if (!inputData.income?.k1_entries) {
      const k1Entries = [];
      if (inputData.income?.k1_qbi_income > 0) {
        k1Entries.push({
          business_name: "K-1 Income",
          qbi_income: inputData.income.k1_qbi_income,
          is_sstb: inputData.income.k1_is_sstb || false,
          subject_to_se_tax: inputData.income.k1_subject_to_se_tax || false,
          w2_wages: inputData.income.k1_w2_wages || 0,
          ubia: inputData.income.k1_ubia || 0
        });
      }
      inputData.income = { ...inputData.income, k1_entries: k1Entries };
    }
    
    setFormData(inputData);
    setResults(projection.results);
    setSelectedProjection(projection);
    setClientName(projection.client_name);
    setNotes(projection.notes || "");
    setShowViewDialog(false);
    toast.success(`Loaded projection for ${projection.client_name}`);
  };

  const deleteProjection = async (id) => {
    if (!window.confirm("Are you sure you want to delete this projection?")) return;
    
    try {
      await taxCalculatorApi.deleteProjection(id);
      toast.success("Projection deleted");
      loadProjections();
      if (selectedProjection?.id === id) {
        setSelectedProjection(null);
        resetForm();
      }
    } catch (error) {
      toast.error("Failed to delete projection");
    }
  };

  const resetForm = () => {
    setFormData({
      client_name: "",
      filing_status: "single",
      is_65_or_older: false,
      spouse_is_65_or_older: false,
      is_blind: false,
      spouse_is_blind: false,
      taxpayer_covered_by_retirement_plan: true,
      spouse_covered_by_retirement_plan: true,
      hdhp_enrolled: false,
      se_retirement_contribution: 0,
      income: {
        // Multiple W-2s with taxpayer/spouse designation
        w2_entries: [
          { label: "Taxpayer W-2", belongs_to: "taxpayer", gross_wages: 0, social_security_wages: 0, social_security_withheld: 0, federal_withholding: 0, covered_by_retirement_plan: false }
        ],
        // Multiple K-1s
        k1_entries: [],
        // Legacy fields
        w2_primary_gross: 0,
        w2_secondary_gross: 0,
        w2_federal_withholding: 0,
        covered_by_retirement_plan: false,
        spouse_w2_primary_gross: 0,
        spouse_w2_secondary_gross: 0,
        spouse_w2_federal_withholding: 0,
        spouse_covered_by_retirement_plan: false,
        // Other income
        social_security_benefits: 0,
        interest_income: 0,
        tax_exempt_interest: 0,
        dividend_income: 0,
        qualified_dividends: 0,
        short_term_capital_gains: 0,
        long_term_capital_gains: 0,
        capital_loss_carryover: 0,
        pension_annuity_income: 0,
        rental_net_income: 0,
        farm_net_income: 0,
        farm_is_sstb: false,
        state_tax_refund: 0,
        other_income: 0,
        schedule_c_entries: [],
        schedule_c_net_income: 0,
        schedule_c_is_sstb: false,
        schedule_c_w2_wages: 0,
        k1_qbi_income: 0,
        k1_is_sstb: false,
        k1_subject_to_se_tax: false,
        k1_w2_wages: 0,
        k1_ubia: 0,
        reit_ptp_distributions: 0,
      },
      deductions: {
        mortgage_interest: 0,
        state_income_tax: 0,
        local_income_tax: 0,
        property_tax: 0,
        personal_property_tax: 0,
        sales_tax: 0,
        medical_expenses: 0,
        charitable_cash: 0,
        charitable_noncash: 0,
        health_insurance_premium: 0,
        student_loan_interest: 0,
        educator_expenses: 0,
        ira_contribution: 0,
        hsa_contribution: 0,
        qualified_overtime: 0,
        qualified_tips: 0,
      },
      credits: {
        num_children_under_17: 0,
        other_dependents: 0,
        estimated_tax_paid: 0,
        dependent_care_expenses: 0,
        num_dependents_for_care: 0,
        education_expenses: 0,
        num_students: 0,
        use_lifetime_learning: false,
        aca_advance_ptc: 0,
        aca_annual_benchmark_premium: 0,
      },
      use_itemized_deductions: null,
    });
    setResults(null);
    setSelectedProjection(null);
    setClientName("");
    setNotes("");
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>2026 Tax Projection - ${clientName || "Client"}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #1e40af; border-bottom: 2px solid #1e40af; padding-bottom: 10px; }
            h2 { color: #334155; margin-top: 24px; }
            .section { margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th, td { padding: 8px 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
            th { background: #f1f5f9; font-weight: 600; }
            .amount { text-align: right; font-family: monospace; }
            .positive { color: #16a34a; }
            .negative { color: #dc2626; }
            .total-row { font-weight: bold; background: #f8fafc; }
            .summary-box { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px; padding: 16px; margin: 16px 0; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 12px; color: #64748b; }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleDateString()} | Steele Martin Jones CPAs</p>
            <p>This is a tax projection only. Actual tax liability may differ based on final figures and tax law changes.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const isMarried = formData.filing_status === "married_joint" || formData.filing_status === "married_separate";

  // Firm logo URL (for client report)
  const FIRM_LOGO_URL = "https://customer-assets.emergentagent.com/job_stat-dashboard-6/artifacts/byzfn9fi_Logo%20with%20Words.jpg";

  return (
    <div className="space-y-6" data-testid="tax-calculator-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">2026 Tax Projection</h1>
          <p className="text-muted-foreground mt-1">
            Federal tax calculator with OBBBA updates
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowViewDialog(true)} data-testid="view-saved-btn">
            <FileText size={18} className="mr-2" />
            Saved ({projections.length})
          </Button>
          <Button variant="outline" onClick={resetForm} data-testid="new-projection-btn">
            <Plus size={18} className="mr-2" />
            New
          </Button>
          {results && (
            <>
              <Button 
                variant="outline" 
                onClick={() => {
                  // Pre-populate with form client name or existing projection name
                  if (selectedProjection) {
                    setClientName(selectedProjection.client_name);
                    setNotes(selectedProjection.notes || "");
                  } else if (formData.client_name) {
                    setClientName(formData.client_name);
                  }
                  setShowSaveDialog(true);
                }}
                data-testid="save-projection-btn"
              >
                <Save size={18} className="mr-2" />
                Save
              </Button>
              <Button variant="outline" onClick={handlePrint} data-testid="print-btn">
                <Printer size={18} className="mr-2" />
                Print
              </Button>
              <Button 
                variant="default"
                onClick={() => setShowReportDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-report-btn"
              >
                <FileText size={18} className="mr-2" />
                Client Report
              </Button>
            </>
          )}
        </div>
      </div>

      {selectedProjection && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 flex items-center justify-between">
          <span className="text-blue-800">
            Editing: <strong>{selectedProjection.client_name}</strong>
          </span>
          <Button variant="ghost" size="sm" onClick={resetForm}>
            Clear
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Inputs */}
        <div className="space-y-4">
          {/* Filing Status & Personal Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <User size={20} className="text-blue-600" />
                Filing Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Client Name */}
              <div className="space-y-1">
                <Label htmlFor="client_name_input">Client Name</Label>
                <Input
                  id="client_name_input"
                  value={formData.client_name}
                  onChange={(e) => updateFormField(null, "client_name", e.target.value)}
                  placeholder="Enter client name"
                  data-testid="client-name-input"
                />
              </div>

              <div className="space-y-1">
                <Label>Filing Status</Label>
                <Select
                  value={formData.filing_status}
                  onValueChange={(v) => updateFormField(null, "filing_status", v)}
                >
                  <SelectTrigger data-testid="filing-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILING_STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dependents */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="num_children">Children Under 17</Label>
                  <Input
                    id="num_children"
                    type="number"
                    min="0"
                    value={formData.credits.num_children_under_17 || ""}
                    onChange={(e) => updateFormField("credits", "num_children_under_17", parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="other_dep">Other Dependents</Label>
                  <Input
                    id="other_dep"
                    type="number"
                    min="0"
                    value={formData.credits.other_dependents || ""}
                    onChange={(e) => updateFormField("credits", "other_dependents", parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_65"
                    checked={formData.is_65_or_older}
                    onCheckedChange={(v) => updateFormField(null, "is_65_or_older", v)}
                  />
                  <Label htmlFor="is_65" className="text-sm">Age 65 or older</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_blind"
                    checked={formData.is_blind}
                    onCheckedChange={(v) => updateFormField(null, "is_blind", v)}
                  />
                  <Label htmlFor="is_blind" className="text-sm">Blind</Label>
                </div>
              </div>

              {isMarried && (
                <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="spouse_65"
                      checked={formData.spouse_is_65_or_older}
                      onCheckedChange={(v) => updateFormField(null, "spouse_is_65_or_older", v)}
                    />
                    <Label htmlFor="spouse_65" className="text-sm">Spouse 65+</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="spouse_blind"
                      checked={formData.spouse_is_blind}
                      onCheckedChange={(v) => updateFormField(null, "spouse_is_blind", v)}
                    />
                    <Label htmlFor="spouse_blind" className="text-sm">Spouse Blind</Label>
                  </div>
                </div>
              )}

              {/* Workplace plan coverage (drives IRA deductibility) and HDHP enrollment (gates HSA). */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/50 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="tp_covered"
                    checked={formData.taxpayer_covered_by_retirement_plan}
                    onCheckedChange={(v) => updateFormField(null, "taxpayer_covered_by_retirement_plan", v)}
                  />
                  <Label htmlFor="tp_covered" className="text-sm">
                    Taxpayer covered by workplace retirement plan
                    <span className="block text-xs text-muted-foreground">(W-2 Box 13 — drives IRA deductibility)</span>
                  </Label>
                </div>
                {formData.filing_status === "married_joint" && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="sp_covered"
                      checked={formData.spouse_covered_by_retirement_plan}
                      onCheckedChange={(v) => updateFormField(null, "spouse_covered_by_retirement_plan", v)}
                    />
                    <Label htmlFor="sp_covered" className="text-sm">
                      Spouse covered by workplace retirement plan
                      <span className="block text-xs text-muted-foreground">(W-2 Box 13)</span>
                    </Label>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="hdhp_enrolled"
                    checked={formData.hdhp_enrolled}
                    onCheckedChange={(v) => updateFormField(null, "hdhp_enrolled", v)}
                  />
                  <Label htmlFor="hdhp_enrolled" className="text-sm">
                    Enrolled in HDHP (HSA-eligible)
                    <span className="block text-xs text-muted-foreground">(required for HSA contributions)</span>
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Income Section */}
          <CollapsibleSection title="Income" icon={DollarSign} defaultOpen={true}>
            <div className="space-y-4">
              {/* W-2 Entries */}
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-foreground">W-2 Wages</h4>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={addW2Entry}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add W-2
                </Button>
              </div>
              
              {formData.income.w2_entries.map((w2, index) => (
                <div key={index} className="p-3 border rounded-lg bg-card space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1">
                      {formData.filing_status === "married_joint" && (
                        <Select
                          value={w2.belongs_to || "taxpayer"}
                          onValueChange={(v) => updateW2Entry(index, 'belongs_to', v)}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="taxpayer">Taxpayer</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <Input
                        value={w2.label}
                        onChange={(e) => updateW2Entry(index, 'label', e.target.value)}
                        placeholder="W-2 Description (e.g., Main Job)"
                        className="text-sm font-medium bg-card max-w-xs h-8"
                      />
                    </div>
                    {formData.income.w2_entries.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeW2Entry(index)}
                        className="h-7 text-red-500 hover:text-red-700 hover:bg-red-500/20"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyInput
                      id={`w2_gross_${index}`}
                      label="Gross Wages (Box 1)"
                      value={w2.gross_wages}
                      onChange={(v) => updateW2Entry(index, 'gross_wages', v)}
                    />
                    <CurrencyInput
                      id={`w2_withholding_${index}`}
                      label="Federal Withholding (Box 2)"
                      value={w2.federal_withholding}
                      onChange={(v) => updateW2Entry(index, 'federal_withholding', v)}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`w2_retirement_${index}`}
                      checked={w2.covered_by_retirement_plan}
                      onCheckedChange={(v) => updateW2Entry(index, 'covered_by_retirement_plan', v)}
                    />
                    <Label htmlFor={`w2_retirement_${index}`} className="text-sm text-muted-foreground">
                      Covered by retirement plan at work (Box 13)
                    </Label>
                  </div>
                </div>
              ))}
              
              {/* Info about excess FICA */}
              {formData.filing_status === "married_joint" && (
                <p className="text-xs text-muted-foreground bg-blue-500/10 p-2 rounded border border-blue-500/20">
                  <strong>Excess FICA:</strong> Each spouse has their own SS wage base ($176,100 for 2026). 
                  If either has multiple W-2s and SS tax withheld exceeds what's required, the excess can be claimed as a credit.
                </p>
              )}
              
              {/* Info about IRA deductibility */}
              <p className="text-xs text-muted-foreground bg-muted p-2 rounded border border-border">
                Retirement plan coverage affects Traditional IRA deduction eligibility
              </p>

              <h4 className="font-medium text-foreground pt-2 flex items-center gap-2">
                Retirement Income
                <span className="text-xs font-normal text-muted-foreground">(Social Security, Pensions)</span>
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="social_security"
                  label="Social Security Benefits (1099-SSA)"
                  value={formData.income.social_security_benefits}
                  onChange={(v) => updateFormField("income", "social_security_benefits", v)}
                />
                <CurrencyInput
                  id="pension"
                  label="Pension / Annuity (1099-R)"
                  value={formData.income.pension_annuity_income}
                  onChange={(v) => updateFormField("income", "pension_annuity_income", v)}
                />
              </div>

              <h4 className="font-medium text-foreground pt-2">Investment Income</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="interest"
                  label="Taxable Interest"
                  value={formData.income.interest_income}
                  onChange={(v) => updateFormField("income", "interest_income", v)}
                />
                <CurrencyInput
                  id="tax_exempt_interest"
                  label="Tax-Exempt Interest (Muni Bonds)"
                  value={formData.income.tax_exempt_interest}
                  onChange={(v) => updateFormField("income", "tax_exempt_interest", v)}
                />
                <CurrencyInput
                  id="dividends"
                  label="Ordinary Dividends"
                  value={formData.income.dividend_income}
                  onChange={(v) => updateFormField("income", "dividend_income", v)}
                />
                <CurrencyInput
                  id="qual_div"
                  label="Qualified Dividends"
                  value={formData.income.qualified_dividends}
                  onChange={(v) => updateFormField("income", "qualified_dividends", v)}
                />
                <CurrencyInput
                  id="st_gains"
                  label="Short-Term Cap Gains"
                  value={formData.income.short_term_capital_gains}
                  onChange={(v) => updateFormField("income", "short_term_capital_gains", v)}
                />
                <CurrencyInput
                  id="lt_gains"
                  label="Long-Term Cap Gains"
                  value={formData.income.long_term_capital_gains}
                  onChange={(v) => updateFormField("income", "long_term_capital_gains", v)}
                />
                <CurrencyInput
                  id="cap_loss"
                  label="Capital Loss Carryover"
                  value={formData.income.capital_loss_carryover}
                  onChange={(v) => updateFormField("income", "capital_loss_carryover", v)}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  Self-Employment (Schedule C)
                  <span className="text-xs font-normal text-muted-foreground">(QBI eligible)</span>
                </h4>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={addScheduleCEntry}
                  className="h-7 text-xs"
                  data-testid="add-schedule-c"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Schedule C
                </Button>
              </div>

              {(formData.income.schedule_c_entries || []).length === 0 && (
                <p className="text-xs text-muted-foreground italic">
                  No Schedule C yet — click &quot;Add Schedule C&quot; for each sole-prop business
                  this taxpayer (and/or spouse) operates.
                </p>
              )}

              {(formData.income.schedule_c_entries || []).map((sc, index) => (
                <div
                  key={`sc-${index}`}
                  className="p-3 bg-muted/30 rounded-lg border border-border/50 space-y-3"
                  data-testid={`schedule-c-entry-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <Input
                      value={sc.business_name}
                      onChange={(e) => updateScheduleCEntry(index, 'business_name', e.target.value)}
                      placeholder="Business Name (e.g., Smith Consulting)"
                      className="text-sm font-medium bg-card max-w-xs h-8"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeScheduleCEntry(index)}
                      className="h-7 text-red-500 hover:text-red-700 hover:bg-red-500/20"
                      data-testid={`remove-schedule-c-${index}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {formData.filing_status === "married_joint" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Whose Schedule C?</Label>
                        <Select
                          value={sc.belongs_to || "taxpayer"}
                          onValueChange={(v) => updateScheduleCEntry(index, 'belongs_to', v)}
                        >
                          <SelectTrigger className="h-8" data-testid={`schedule-c-belongs-to-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="taxpayer">Taxpayer</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyInput
                      id={`sched_c_net_${index}`}
                      label="Net Profit (or Loss)"
                      value={sc.net_income}
                      onChange={(v) => updateScheduleCEntry(index, 'net_income', v)}
                    />
                    <CurrencyInput
                      id={`sched_c_w2_${index}`}
                      label="W-2 Wages Paid by Business"
                      value={sc.w2_wages}
                      onChange={(v) => updateScheduleCEntry(index, 'w2_wages', v)}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`sched_c_sstb_${index}`}
                      checked={sc.is_sstb}
                      onCheckedChange={(v) => updateScheduleCEntry(index, 'is_sstb', v)}
                    />
                    <Label htmlFor={`sched_c_sstb_${index}`} className="text-sm">
                      Specified Service Trade or Business (SSTB)
                      <span className="block text-xs text-muted-foreground">
                        (law, accounting, health, consulting, athletics, financial services, etc.)
                      </span>
                    </Label>
                  </div>
                </div>
              ))}

              <h4 className="font-medium text-foreground pt-2">Rental &amp; Farm Income</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="rental_net"
                  label="Rental Net Income (Schedule E)"
                  value={formData.income.rental_net_income}
                  onChange={(v) => updateFormField("income", "rental_net_income", v)}
                />
                <CurrencyInput
                  id="farm_net"
                  label="Farm Net Income (Schedule F)"
                  value={formData.income.farm_net_income}
                  onChange={(v) => updateFormField("income", "farm_net_income", v)}
                />
              </div>
              {formData.income.farm_net_income > 0 && formData.filing_status === "married_joint" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-sm">Whose Farm income is this?</Label>
                    <Select
                      value={formData.income.farm_belongs_to || "taxpayer"}
                      onValueChange={(v) => updateFormField("income", "farm_belongs_to", v)}
                    >
                      <SelectTrigger data-testid="farm-belongs-to">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="taxpayer">Taxpayer</SelectItem>
                        <SelectItem value="spouse">Spouse</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-muted-foreground">
                      Determines whose SS wage base applies for the SE tax SS portion.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <h4 className="font-medium text-foreground flex items-center gap-2">
                  K-1 &amp; Pass-Through Income
                  <span className="text-xs font-normal text-muted-foreground">(QBI eligible)</span>
                </h4>
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={addK1Entry}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add K-1
                </Button>
              </div>
              
              {formData.income.k1_entries.length === 0 && (
                <p className="text-sm text-muted-foreground italic p-3 bg-card rounded">
                  No K-1 entries. Click "Add K-1" to add partnership or S-corp income.
                </p>
              )}
              
              {formData.income.k1_entries.map((k1, index) => (
                <div key={index} className="p-3 border rounded-lg bg-amber-500/10 border-amber-500/30 space-y-3">
                  <div className="flex items-center justify-between">
                    <Input
                      value={k1.business_name}
                      onChange={(e) => updateK1Entry(index, 'business_name', e.target.value)}
                      placeholder="Business Name (e.g., ABC Partnership)"
                      className="text-sm font-medium bg-card max-w-xs h-8"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeK1Entry(index)}
                      className="h-7 text-red-500 hover:text-red-700 hover:bg-red-500/20"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>

                  {formData.filing_status === "married_joint" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-muted-foreground">Whose K-1?</Label>
                        <Select
                          value={k1.belongs_to || "taxpayer"}
                          onValueChange={(v) => updateK1Entry(index, 'belongs_to', v)}
                        >
                          <SelectTrigger className="h-8" data-testid={`k1-belongs-to-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="taxpayer">Taxpayer</SelectItem>
                            <SelectItem value="spouse">Spouse</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    <CurrencyInput
                      id={`k1_income_${index}`}
                      label="K-1 QBI Income"
                      value={k1.qbi_income}
                      onChange={(v) => updateK1Entry(index, 'qbi_income', v)}
                    />
                    <CurrencyInput
                      id={`k1_w2_${index}`}
                      label="W-2 Wages Paid by Business"
                      value={k1.w2_wages}
                      onChange={(v) => updateK1Entry(index, 'w2_wages', v)}
                    />
                  </div>
                  
                  <CurrencyInput
                    id={`k1_ubia_${index}`}
                    label="UBIA of Qualified Property"
                    value={k1.ubia}
                    onChange={(v) => updateK1Entry(index, 'ubia', v)}
                  />
                  
                  <div className="grid grid-cols-2 gap-3 pt-1">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`k1_se_tax_${index}`}
                        checked={k1.subject_to_se_tax}
                        onCheckedChange={(v) => updateK1Entry(index, 'subject_to_se_tax', v)}
                      />
                      <Label htmlFor={`k1_se_tax_${index}`} className="text-sm">
                        Subject to SE Tax
                        <span className="block text-xs text-muted-foreground">
                          (General partner/material participation)
                        </span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`k1_sstb_${index}`}
                        checked={k1.is_sstb}
                        onCheckedChange={(v) => updateK1Entry(index, 'is_sstb', v)}
                      />
                      <Label htmlFor={`k1_sstb_${index}`} className="text-sm">
                        SSTB
                        <span className="block text-xs text-muted-foreground">
                          (Professional services business)
                        </span>
                      </Label>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="grid grid-cols-2 gap-3 pt-2">
                <CurrencyInput
                  id="reit"
                  label="REIT/PTP Distributions"
                  value={formData.income.reit_ptp_distributions}
                  onChange={(v) => updateFormField("income", "reit_ptp_distributions", v)}
                />
              </div>

              <h4 className="font-medium text-foreground pt-2">Other Income</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="state_refund"
                  label="State Tax Refund"
                  value={formData.income.state_tax_refund}
                  onChange={(v) => updateFormField("income", "state_tax_refund", v)}
                />
                <CurrencyInput
                  id="other_inc"
                  label="Other Income"
                  value={formData.income.other_income}
                  onChange={(v) => updateFormField("income", "other_income", v)}
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* Deductions Section */}
          <CollapsibleSection title="Deductions" icon={Building2} defaultOpen={false}>
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Itemized Deductions</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="mortgage"
                  label="Mortgage Interest"
                  value={formData.deductions.mortgage_interest}
                  onChange={(v) => updateFormField("deductions", "mortgage_interest", v)}
                />
                <CurrencyInput
                  id="state_tax"
                  label="State Income Tax"
                  value={formData.deductions.state_income_tax}
                  onChange={(v) => updateFormField("deductions", "state_income_tax", v)}
                />
                <CurrencyInput
                  id="local_tax"
                  label="Local Income Tax"
                  value={formData.deductions.local_income_tax}
                  onChange={(v) => updateFormField("deductions", "local_income_tax", v)}
                />
                <CurrencyInput
                  id="property_tax"
                  label="Property Tax"
                  value={formData.deductions.property_tax}
                  onChange={(v) => updateFormField("deductions", "property_tax", v)}
                />
                <CurrencyInput
                  id="sales_tax"
                  label="Sales Tax"
                  value={formData.deductions.sales_tax}
                  onChange={(v) => updateFormField("deductions", "sales_tax", v)}
                />
                <CurrencyInput
                  id="medical"
                  label="Medical Expenses"
                  value={formData.deductions.medical_expenses}
                  onChange={(v) => updateFormField("deductions", "medical_expenses", v)}
                />
                <CurrencyInput
                  id="charity_cash"
                  label="Charitable (Cash)"
                  value={formData.deductions.charitable_cash}
                  onChange={(v) => updateFormField("deductions", "charitable_cash", v)}
                />
                <CurrencyInput
                  id="charity_noncash"
                  label="Charitable (Non-cash)"
                  value={formData.deductions.charitable_noncash}
                  onChange={(v) => updateFormField("deductions", "charitable_noncash", v)}
                />
              </div>

              <h4 className="font-medium text-foreground pt-2">Above-the-Line Deductions</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="health_ins"
                  label="Health Insurance (SE)"
                  value={formData.deductions.health_insurance_premium}
                  onChange={(v) => updateFormField("deductions", "health_insurance_premium", v)}
                />
                <CurrencyInput
                  id="student_loan"
                  label="Student Loan Interest"
                  value={formData.deductions.student_loan_interest}
                  onChange={(v) => updateFormField("deductions", "student_loan_interest", v)}
                />
                <CurrencyInput
                  id="educator"
                  label="Educator Expenses"
                  value={formData.deductions.educator_expenses}
                  onChange={(v) => updateFormField("deductions", "educator_expenses", v)}
                />
                <CurrencyInput
                  id="ira"
                  label="IRA Contribution"
                  value={formData.deductions.ira_contribution}
                  onChange={(v) => updateFormField("deductions", "ira_contribution", v)}
                />
                <CurrencyInput
                  id="hsa"
                  label="HSA Contribution"
                  value={formData.deductions.hsa_contribution}
                  onChange={(v) => updateFormField("deductions", "hsa_contribution", v)}
                />
              </div>
              
              <h4 className="font-medium text-foreground pt-2">2026 OBBBA Deductions</h4>
              <p className="text-xs text-muted-foreground mb-2">No Tax on Overtime & Tips provisions</p>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="qualified_overtime"
                  label="Qualified Overtime"
                  value={formData.deductions.qualified_overtime}
                  onChange={(v) => updateFormField("deductions", "qualified_overtime", v)}
                />
                <CurrencyInput
                  id="qualified_tips"
                  label="Qualified Tips"
                  value={formData.deductions.qualified_tips}
                  onChange={(v) => updateFormField("deductions", "qualified_tips", v)}
                />
              </div>

              <div className="pt-2 border-t">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="force_itemize"
                    checked={formData.use_itemized_deductions === true}
                    onCheckedChange={(v) => updateFormField(null, "use_itemized_deductions", v ? true : null)}
                  />
                  <Label htmlFor="force_itemize" className="text-sm">
                    Force itemized deductions (auto-select if unchecked)
                  </Label>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* Credits Section */}
          <CollapsibleSection title="Credits & Payments" icon={TrendingUp} defaultOpen={false}>
            <div className="space-y-4">
              <h4 className="font-medium text-foreground">Dependent Care Credit</h4>
              <p className="text-xs text-muted-foreground">For childcare or dependent care expenses while working</p>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="dep_care_exp"
                  label="Dependent Care Expenses Paid"
                  value={formData.credits.dependent_care_expenses}
                  onChange={(v) => updateFormField("credits", "dependent_care_expenses", v)}
                />
                <div className="space-y-1">
                  <Label htmlFor="num_care_dep">Number of Dependents in Care</Label>
                  <Input
                    id="num_care_dep"
                    type="number"
                    min="0"
                    max="2"
                    value={formData.credits.num_dependents_for_care || ""}
                    onChange={(e) => updateFormField("credits", "num_dependents_for_care", parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              <h4 className="font-medium text-foreground pt-2">Education Credits</h4>
              <p className="text-xs text-muted-foreground">AOTC (undergrad) or Lifetime Learning (grad/continuing ed)</p>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="edu_exp"
                  label="Qualified Education Expenses"
                  value={formData.credits.education_expenses}
                  onChange={(v) => updateFormField("credits", "education_expenses", v)}
                />
                <div className="space-y-1">
                  <Label htmlFor="num_students">Number of Students</Label>
                  <Input
                    id="num_students"
                    type="number"
                    min="0"
                    value={formData.credits.num_students || ""}
                    onChange={(e) => updateFormField("credits", "num_students", parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
              {formData.credits.education_expenses > 0 && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="use_llc"
                    checked={formData.credits.use_lifetime_learning}
                    onCheckedChange={(v) => updateFormField("credits", "use_lifetime_learning", v)}
                  />
                  <Label htmlFor="use_llc" className="text-sm">
                    Use Lifetime Learning Credit instead of AOTC
                    <span className="block text-xs text-muted-foreground">
                      (For graduate students, professional degrees, or students who've claimed AOTC 4+ years)
                    </span>
                  </Label>
                </div>
              )}

              <h4 className="font-medium text-foreground pt-2">Tax Payments</h4>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="est_tax"
                  label="Estimated Tax Paid"
                  value={formData.credits.estimated_tax_paid}
                  onChange={(v) => updateFormField("credits", "estimated_tax_paid", v)}
                />
              </div>

              <h4 className="font-medium text-foreground pt-2">ACA Premium Tax Credit</h4>
              <p className="text-xs text-muted-foreground mb-2">
                For clients receiving Marketplace health insurance subsidies. Calculate potential repayment based on actual income.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <CurrencyInput
                  id="aca_aptc"
                  label="Advance PTC Received"
                  value={formData.credits.aca_advance_ptc}
                  onChange={(v) => updateFormField("credits", "aca_advance_ptc", v)}
                />
                <CurrencyInput
                  id="aca_slcsp"
                  label="Annual Benchmark Premium"
                  value={formData.credits.aca_annual_benchmark_premium}
                  onChange={(v) => updateFormField("credits", "aca_annual_benchmark_premium", v)}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Benchmark = Second Lowest Cost Silver Plan (SLCSP) from Form 1095-A. Household size calculated from filing status + dependents.
              </p>
            </div>
          </CollapsibleSection>

          {/* Calculate Button */}
          <Button 
            onClick={calculateTax} 
            disabled={calculating}
            className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-lg"
            data-testid="calculate-btn"
          >
            {calculating ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="mr-2 h-5 w-5" />
                Calculate Tax
              </>
            )}
          </Button>
        </div>

        {/* Right Column - Results */}
        <div className="space-y-4">
          {results ? (
            <div ref={printRef}>
              <h1 className="text-2xl font-bold text-blue-800 mb-4 print:block hidden">
                2026 Tax Projection {clientName && `- ${clientName}`}
              </h1>

              {/* Summary Card */}
              <Card className="border-2 border-blue-500/30 bg-blue-500/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-blue-800">Tax Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Income</p>
                      <p className="text-xl font-bold">{formatCurrency(results.total_income)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">AGI</p>
                      <p className="text-xl font-bold">{formatCurrency(results.adjusted_gross_income)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Taxable Income</p>
                      <p className="text-xl font-bold">{formatCurrency(results.taxable_income)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Tax</p>
                      <p className="text-xl font-bold">{formatCurrency(results.total_tax_liability)}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-medium">
                        {results.tax_due_or_refund >= 0 ? "Tax Due" : "Refund"}
                      </span>
                      <span className={`text-2xl font-bold ${results.tax_due_or_refund >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {formatCurrency(Math.abs(results.tax_due_or_refund))}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground mt-2">
                      <span>Effective Rate: {formatPercent(results.effective_tax_rate)}</span>
                      <span>Marginal Rate: {formatPercent(results.marginal_tax_rate)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tax Bracket Planning */}
              {results.income_until_next_bracket > 0 && results.next_bracket_rate > 0 && (
                <Card className="mt-4 border-teal-200 bg-teal-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-teal-800">Tax Bracket Planning</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="p-3 bg-card rounded-lg border border-teal-200">
                        <div className="flex justify-between items-center">
                          <span className="text-foreground">Current Tax Bracket</span>
                          <span className="font-bold text-teal-700">{formatPercent(results.current_tax_bracket_rate)}</span>
                        </div>
                      </div>
                      <div className="p-3 bg-teal-100 rounded-lg border border-teal-300">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-teal-800">Income Until Next Bracket</span>
                          <span className="font-bold text-teal-700">{formatCurrency(results.income_until_next_bracket)}</span>
                        </div>
                        <p className="text-sm text-teal-700">
                          You can earn up to {formatCurrency(results.income_until_next_bracket)} more before 
                          moving into the {formatPercent(results.next_bracket_rate)} tax bracket.
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground italic">
                        * Based on taxable income of {formatCurrency(results.taxable_income)}. Bracket top: {formatCurrency(results.current_bracket_top)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Social Security Taxation */}
              {results.ss_benefits_received > 0 && (
                <Card className="mt-4 border-amber-500/30 bg-amber-500/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-amber-800">Social Security Taxation</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Total SS Benefits Received</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.ss_benefits_received)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Combined Income (for SS calc)</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.ss_combined_income)}
                          </TableCell>
                        </TableRow>
                        <TableRow className="bg-amber-100">
                          <TableCell className="font-medium">
                            Taxable Portion ({results.ss_taxable_percent}%)
                          </TableCell>
                          <TableCell className="text-right font-mono font-bold">
                            {formatCurrency(results.ss_taxable_amount)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="text-emerald-700">Tax-Free Portion</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">
                            {formatCurrency(results.ss_benefits_received - results.ss_taxable_amount)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* IRMAA (Medicare Premium Impact) - Only show for 65+ */}
              {results.irmaa_applicable && (
              <Card className="mt-4 border-purple-200 bg-purple-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-purple-800">Medicare Premium Impact (IRMAA)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">MAGI (for IRMAA)</span>
                      <span className="font-mono font-medium">{formatCurrency(results.magi_for_irmaa)}</span>
                    </div>
                    <div className={`p-3 rounded-lg ${results.irmaa_tier > 0 ? "bg-rose-100 border border-rose-300" : "bg-emerald-100 border border-emerald-300"}`}>
                      <div className="flex justify-between items-center">
                        <span className={`font-medium ${results.irmaa_tier > 0 ? "text-rose-800" : "text-emerald-800"}`}>
                          {results.irmaa_tier_label}
                        </span>
                        {results.annual_irmaa_cost > 0 && (
                          <span className="text-rose-700 font-bold">
                            +{formatCurrency(results.annual_irmaa_cost)}/year
                          </span>
                        )}
                      </div>
                    </div>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell>Part B Monthly Premium</TableCell>
                          <TableCell className="text-right font-mono">
                            ${results.part_b_monthly_premium?.toFixed(2)}
                          </TableCell>
                        </TableRow>
                        {results.part_b_irmaa_surcharge > 0 && (
                          <TableRow>
                            <TableCell className="text-rose-600">Part B IRMAA Surcharge</TableCell>
                            <TableCell className="text-right font-mono text-rose-600">
                              +${results.part_b_irmaa_surcharge?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                        {results.part_d_irmaa_surcharge > 0 && (
                          <TableRow>
                            <TableCell className="text-rose-600">Part D IRMAA Surcharge</TableCell>
                            <TableCell className="text-right font-mono text-rose-600">
                              +${results.part_d_irmaa_surcharge?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    {results.irmaa_headroom > 0 && (
                      <div className="mt-3 p-3 bg-blue-500/20 rounded-lg border border-blue-500/40">
                        <p className="text-sm text-blue-800">
                          <strong>Planning Note:</strong> You have {formatCurrency(results.irmaa_headroom)} of "headroom" 
                          before reaching the next IRMAA tier at {formatCurrency(results.irmaa_current_bracket_max)}.
                        </p>
                      </div>
                    )}
                    {results.irmaa_tier > 0 && results.irmaa_next_bracket_threshold > 0 && (
                      <div className="mt-2 p-3 bg-amber-100 rounded-lg border border-amber-300">
                        <p className="text-sm text-amber-800">
                          <strong>Warning:</strong> Your income triggers IRMAA surcharges. Consider strategies to reduce MAGI 
                          (Roth conversions timing, charitable giving, etc.) to potentially reduce Medicare premiums.
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Deductions Breakdown */}
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Deductions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Standard Deduction</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(results.standard_deduction)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Itemized Deductions</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(results.itemized_deductions)}
                        </TableCell>
                      </TableRow>
                      <TableRow className="bg-blue-500/10">
                        <TableCell className="font-medium">
                          Using: {results.deduction_used === "standard" ? "Standard" : "Itemized"}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(results.deduction_used === "standard" ? results.standard_deduction : results.itemized_deductions)}
                        </TableCell>
                      </TableRow>
                      {results.senior_bonus_deduction > 0 && (
                        <TableRow>
                          <TableCell className="text-emerald-700">Senior Bonus Deduction (OBBBA)</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">
                            {formatCurrency(results.senior_bonus_deduction)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.above_line_charitable > 0 && (
                        <TableRow>
                          <TableCell className="text-emerald-700">Charitable (Above-the-Line)</TableCell>
                          <TableCell className="text-right font-mono text-emerald-700">
                            {formatCurrency(results.above_line_charitable)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.qbi_deduction > 0 && (
                        <TableRow>
                          <TableCell>
                            QBI Deduction (Section 199A)
                            {results.qbi_is_sstb && <span className="text-amber-600 text-xs ml-1">(SSTB)</span>}
                            {results.qbi_limited_by_income && <span className="text-amber-600 text-xs ml-1">(income limited)</span>}
                            {results.qbi_limited_by_wages && <span className="text-amber-600 text-xs ml-1">(W-2 limited)</span>}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.qbi_deduction)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Tax Breakdown */}
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Tax Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Ordinary Income Tax</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(results.ordinary_income_tax)}
                        </TableCell>
                      </TableRow>
                      {results.capital_gains_tax > 0 && (
                        <TableRow>
                          <TableCell>Capital Gains Tax</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.capital_gains_tax)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.self_employment_tax > 0 && (
                        <TableRow>
                          <TableCell>Self-Employment Tax</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.self_employment_tax)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.niit_tax > 0 && (
                        <TableRow>
                          <TableCell>Net Investment Income Tax</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.niit_tax)}
                          </TableCell>
                        </TableRow>
                      )}
                      {results.medicare_surtax > 0 && (
                        <TableRow>
                          <TableCell>Additional Medicare Tax</TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(results.medicare_surtax)}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow className="border-t-2">
                        <TableCell className="font-medium">Total Tax Before Credits</TableCell>
                        <TableCell className="text-right font-mono font-bold">
                          {formatCurrency(results.total_tax_before_credits)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Credits */}
              {results.total_credits > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Tax Credits</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableBody>
                        {results.child_tax_credit > 0 && (
                          <TableRow>
                            <TableCell>Child Tax Credit ({formData.credits.num_children_under_17} children × $2,200)</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              -{formatCurrency(results.child_tax_credit)}
                            </TableCell>
                          </TableRow>
                        )}
                        {results.dependent_care_credit > 0 && (
                          <TableRow>
                            <TableCell>Dependent Care Credit</TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              -{formatCurrency(results.dependent_care_credit)}
                            </TableCell>
                          </TableRow>
                        )}
                        {results.education_credit > 0 && (
                          <TableRow>
                            <TableCell>
                              Education Credit ({results.education_credit_type === "AOTC" ? "American Opportunity" : "Lifetime Learning"})
                            </TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              -{formatCurrency(results.education_credit)}
                            </TableCell>
                          </TableRow>
                        )}
                        {results.excess_fica_credit > 0 && (
                          <TableRow>
                            <TableCell>
                              Excess Social Security Tax Withheld
                              {results.excess_fica_taxpayer > 0 && results.excess_fica_spouse > 0 && (
                                <span className="text-xs text-muted-foreground ml-2">
                                  (TP: {formatCurrency(results.excess_fica_taxpayer)}, SP: {formatCurrency(results.excess_fica_spouse)})
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono text-emerald-600">
                              -{formatCurrency(results.excess_fica_credit)}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow className="border-t">
                          <TableCell className="font-medium">Total Credits</TableCell>
                          <TableCell className="text-right font-mono font-bold text-emerald-600">
                            -{formatCurrency(results.total_credits)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Payments & Balance */}
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Payments & Balance</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Tax Liability</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(results.total_tax_liability)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Withholding + Estimated Payments</TableCell>
                        <TableCell className="text-right font-mono">
                          -{formatCurrency(results.total_payments)}
                        </TableCell>
                      </TableRow>
                      <TableRow className={`border-t-2 ${results.tax_due_or_refund >= 0 ? "bg-rose-50" : "bg-emerald-50"}`}>
                        <TableCell className="font-bold">
                          {results.tax_due_or_refund >= 0 ? "Amount Due" : "Refund Amount"}
                        </TableCell>
                        <TableCell className={`text-right font-mono font-bold text-lg ${results.tax_due_or_refund >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                          {formatCurrency(Math.abs(results.tax_due_or_refund))}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Tax Savings Opportunities */}
              {results.tax_savings_suggestions && results.tax_savings_suggestions.length > 0 && (
                <Card className="mt-4 border-amber-500/30 bg-gradient-to-br from-amber-50 to-yellow-50">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg text-amber-800">
                      <Lightbulb size={20} className="text-amber-600" />
                      Tax Savings Opportunities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {results.tax_savings_suggestions.slice(0, 5).map((suggestion, idx) => (
                        <div 
                          key={idx} 
                          className={`p-3 rounded-lg border ${
                            suggestion.priority === "high" 
                              ? "bg-amber-100 border-amber-300" 
                              : suggestion.priority === "medium"
                              ? "bg-yellow-50 border-yellow-200"
                              : "bg-card border-border"
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              suggestion.priority === "high" 
                                ? "bg-amber-200 text-amber-800" 
                                : suggestion.priority === "medium"
                                ? "bg-yellow-200 text-yellow-800"
                                : "bg-muted text-muted-foreground"
                            }`}>
                              {suggestion.category}
                            </span>
                            {suggestion.potential_savings > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                                Save ~{formatCurrency(suggestion.potential_savings)}
                              </span>
                            )}
                          </div>
                          <h4 className="font-medium text-foreground mt-2">{suggestion.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{suggestion.description}</p>
                        </div>
                      ))}
                      {results.tax_savings_suggestions.length > 5 && (
                        <p className="text-sm text-muted-foreground text-center pt-2">
                          + {results.tax_savings_suggestions.length - 5} more suggestions in full report
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <Card className="h-full flex items-center justify-center min-h-[400px]">
              <div className="text-center text-muted-foreground">
                <Calculator size={48} className="mx-auto mb-4" />
                <p className="text-lg">Enter income and deductions</p>
                <p className="text-sm">then click "Calculate Tax"</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Tax Projection</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="client_name">Client Name *</Label>
              <Input
                id="client_name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes..."
              />
            </div>
            <Button onClick={saveProjection} disabled={loading} className="w-full">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {selectedProjection ? "Update Projection" : "Save Projection"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Saved Projections Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Saved Tax Projections</DialogTitle>
          </DialogHeader>
          {projections.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText size={48} className="mx-auto mb-4" />
              <p>No saved projections yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Filing Status</TableHead>
                  <TableHead className="text-right">Tax Due/Refund</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projections.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.client_name}</TableCell>
                    <TableCell className="capitalize">{p.filing_status.replace("_", " ")}</TableCell>
                    <TableCell className={`text-right font-mono ${p.results.tax_due_or_refund >= 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {p.results.tax_due_or_refund >= 0 ? "" : "-"}{formatCurrency(Math.abs(p.results.tax_due_or_refund))}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(p.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => loadProjection(p)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteProjection(p.id)}>
                          <Trash2 size={14} className="text-rose-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* Client Report Dialog - Corvee-Inspired Professional Design */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card">
          <DialogHeader className="sr-only">
            <DialogTitle>2026 Tax Projection Report</DialogTitle>
          </DialogHeader>
          {results && (
            <div id="client-report" className="report-container" style={{ fontFamily: "'Inter', sans-serif" }}>
              {/* Professional Header Bar */}
              <div className="report-header" style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderBottom: '3px solid #1e3a5f',
                paddingBottom: '16px',
                marginBottom: '24px'
              }}>
                <img 
                  src={FIRM_LOGO_URL} 
                  alt="Steele Martin Jones CPAs" 
                  style={{ height: '44px' }}
                />
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>
                    Federal Tax Projection
                  </p>
                  <p style={{ fontSize: '10px', color: '#94a3b8' }}>
                    Tax Year 2026 • {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>

              {/* Client Info Card */}
              <div className="client-info-card" style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '24px',
                color: 'white',
                pageBreakInside: 'avoid'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                  <div>
                    <p style={{ fontSize: '11px', color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '4px' }}>Prepared For</p>
                    <p style={{ fontSize: '22px', fontWeight: '700' }}>{clientName || formData.client_name || "Client Name"}</p>
                    <p style={{ fontSize: '12px', color: '#bfdbfe', marginTop: '4px' }}>
                      {FILING_STATUSES.find(f => f.value === formData.filing_status)?.label}
                      {(formData.credits.num_children_under_17 > 0 || formData.credits.other_dependents > 0) && (
                        <span> • {formData.credits.num_children_under_17 + formData.credits.other_dependents} Dependent(s)</span>
                      )}
                    </p>
                  </div>
                  <div style={{ 
                    background: results.tax_due_or_refund >= 0 ? 'rgba(234, 179, 8, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    textAlign: 'right'
                  }}>
                    <p style={{ fontSize: '10px', color: '#bfdbfe', textTransform: 'uppercase', marginBottom: '4px' }}>
                      {results.tax_due_or_refund >= 0 ? "Estimated Balance Due" : "Estimated Refund"}
                    </p>
                    <p style={{ 
                      fontSize: '28px', 
                      fontWeight: '700',
                      color: results.tax_due_or_refund >= 0 ? '#fde047' : '#86efac'
                    }}>
                      {formatCurrency(Math.abs(results.tax_due_or_refund))}
                    </p>
                  </div>
                </div>
                
                {/* Key Metrics Row */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: '16px',
                  borderTop: '1px solid rgba(255,255,255,0.2)',
                  paddingTop: '16px'
                }}>
                  <div>
                    <p style={{ fontSize: '10px', color: '#93c5fd', textTransform: 'uppercase', marginBottom: '4px' }}>Gross Income</p>
                    <p style={{ fontSize: '18px', fontWeight: '600' }}>{formatCurrency(results.total_income)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '10px', color: '#93c5fd', textTransform: 'uppercase', marginBottom: '4px' }}>Taxable Income</p>
                    <p style={{ fontSize: '18px', fontWeight: '600' }}>{formatCurrency(results.taxable_income)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '10px', color: '#93c5fd', textTransform: 'uppercase', marginBottom: '4px' }}>Total Tax</p>
                    <p style={{ fontSize: '18px', fontWeight: '600' }}>{formatCurrency(results.total_tax_liability)}</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '10px', color: '#93c5fd', textTransform: 'uppercase', marginBottom: '4px' }}>Effective Rate</p>
                    <p style={{ fontSize: '18px', fontWeight: '600' }}>{formatPercent(results.effective_tax_rate)}</p>
                  </div>
                </div>
              </div>

              {/* Tax Computation Section */}
              <div className="computation-section" style={{ 
                background: '#f8fafc', 
                borderRadius: '10px', 
                padding: '20px',
                marginBottom: '20px',
                pageBreakInside: 'avoid'
              }}>
                <h3 style={{ 
                  fontSize: '13px', 
                  fontWeight: '700', 
                  color: '#1e3a5f', 
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{ width: '4px', height: '16px', background: '#1e3a5f', borderRadius: '2px' }}></span>
                  Tax Computation Summary
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                  {/* Left Column - Income & Deductions */}
                  <div>
                    <table style={{ width: '100%', fontSize: '11px' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569' }}>Total Income</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace' }}>{formatCurrency(results.total_income)}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569' }}>Adjustments to Income</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace', color: '#dc2626' }}>({formatCurrency(results.adjustments_to_income || 0)})</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569', fontWeight: '600' }}>Adjusted Gross Income</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', fontFamily: 'monospace' }}>{formatCurrency(results.adjusted_gross_income ?? results.agi)}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569' }}>
                            {results.deduction_used === "standard" ? "Standard Deduction" : "Itemized Deductions"}
                          </td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace', color: '#dc2626' }}>
                            ({formatCurrency(results.deduction_used === "standard" ? results.standard_deduction : results.itemized_deductions)})
                          </td>
                        </tr>
                        {results.qbi_deduction > 0 && (
                          <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '8px 0', color: '#475569' }}>QBI Deduction (Sec. 199A)</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace', color: '#dc2626' }}>({formatCurrency(results.qbi_deduction)})</td>
                          </tr>
                        )}
                        <tr style={{ background: '#e0f2fe', borderRadius: '4px' }}>
                          <td style={{ padding: '10px 8px', color: '#0369a1', fontWeight: '700' }}>Taxable Income</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', fontFamily: 'monospace', color: '#0369a1' }}>{formatCurrency(results.taxable_income)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Right Column - Tax Calculation */}
                  <div>
                    <table style={{ width: '100%', fontSize: '11px' }}>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569' }}>Regular Tax on Ordinary Income</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace' }}>{formatCurrency(results.ordinary_income_tax)}</td>
                        </tr>
                        {results.capital_gains_tax > 0 && (
                          <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '8px 0', color: '#475569' }}>Tax on Capital Gains</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace' }}>{formatCurrency(results.capital_gains_tax)}</td>
                          </tr>
                        )}
                        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 0', color: '#475569', fontWeight: '600' }}>Total Tax Before Credits</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '600', fontFamily: 'monospace' }}>{formatCurrency(results.ordinary_income_tax + (results.capital_gains_tax || 0))}</td>
                        </tr>
                        {results.total_credits > 0 && (
                          <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '8px 0', color: '#059669' }}>Less: Tax Credits</td>
                            <td style={{ padding: '8px 0', textAlign: 'right', fontWeight: '500', fontFamily: 'monospace', color: '#059669' }}>({formatCurrency(results.total_credits)})</td>
                          </tr>
                        )}
                        <tr style={{ background: '#dcfce7', borderRadius: '4px' }}>
                          <td style={{ padding: '10px 8px', color: '#166534', fontWeight: '700' }}>Total Federal Tax Liability</td>
                          <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', fontFamily: 'monospace', color: '#166534' }}>{formatCurrency(results.total_tax_liability)}</td>
                        </tr>
                      </tbody>
                    </table>
                    
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#64748b' }}>
                      <span>Marginal Rate: <strong style={{ color: '#334155' }}>{formatPercent(results.marginal_tax_rate)}</strong></span>
                      <span>Effective Rate: <strong style={{ color: '#334155' }}>{formatPercent(results.effective_tax_rate)}</strong></span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tax Bracket Planning */}
              {results.income_until_next_bracket > 0 && results.next_bracket_rate > 0 && (
                <div className="bracket-section" style={{
                  background: 'linear-gradient(90deg, #f0fdfa 0%, #ecfeff 100%)',
                  border: '1px solid #99f6e4',
                  borderLeft: '4px solid #14b8a6',
                  borderRadius: '0 8px 8px 0',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  pageBreakInside: 'avoid'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ 
                      width: '44px', 
                      height: '44px', 
                      background: '#14b8a6', 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: '700'
                    }}>
                      {Math.round(results.current_tax_bracket_rate)}%
                    </div>
                    <div>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#0f766e', marginBottom: '2px' }}>Current Federal Tax Bracket</p>
                      <p style={{ fontSize: '11px', color: '#475569' }}>
                        You have <strong style={{ color: '#0f766e' }}>{formatCurrency(results.income_until_next_bracket)}</strong> of taxable income remaining before moving into the <strong>{formatPercent(results.next_bracket_rate)}</strong> bracket.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Business Income & QBI Summary */}
              {((formData.income.schedule_c_entries || []).length > 0 || formData.income.schedule_c_net_income > 0 || formData.income.k1_entries.length > 0) && (
                <div className="business-section" style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfeff 100%)',
                  border: '1px solid #a7f3d0',
                  borderLeft: '4px solid #10b981',
                  borderRadius: '0 8px 8px 0',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  pageBreakInside: 'avoid'
                }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#047857', marginBottom: '12px' }}>Business Income & QBI Summary</h4>
                  
                  <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #a7f3d0' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'left', color: '#047857', fontSize: '10px' }}>Source</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', color: '#047857', fontSize: '10px' }}>Net Income</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', color: '#047857', fontSize: '10px' }}>SSTB</th>
                        <th style={{ padding: '8px 4px', textAlign: 'center', color: '#047857', fontSize: '10px' }}>Subject to SE Tax</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', color: '#047857', fontSize: '10px' }}>QBI Eligible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(formData.income.schedule_c_entries || []).map((sc, index) => (
                        sc.net_income !== 0 && (
                          <tr key={`sc-${index}`} style={{ borderBottom: '1px solid #d1fae5' }}>
                            <td style={{ padding: '8px 4px', fontWeight: '500' }}>
                              {sc.business_name || `Schedule C #${index + 1}`}
                              {formData.filing_status === "married_joint" && (
                                <span style={{ marginLeft: '6px', fontSize: '9px', color: '#64748b' }}>
                                  ({sc.belongs_to === 'spouse' ? 'Spouse' : 'Taxpayer'})
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(sc.net_income)}</td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600',
                                background: sc.is_sstb ? '#fef3c7' : '#e0f2fe',
                                color: sc.is_sstb ? '#92400e' : '#0369a1'
                              }}>
                                {sc.is_sstb ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '600', background: '#dcfce7', color: '#166534' }}>Yes</span>
                            </td>
                            <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(sc.net_income)}</td>
                          </tr>
                        )
                      ))}
                      {formData.income.schedule_c_net_income > 0 && (formData.income.schedule_c_entries || []).length === 0 && (
                        <tr style={{ borderBottom: '1px solid #d1fae5' }}>
                          <td style={{ padding: '8px 4px', fontWeight: '500' }}>Schedule C (Self-Employment)</td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(formData.income.schedule_c_net_income)}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: formData.income.schedule_c_is_sstb ? '#fef3c7' : '#e0f2fe',
                              color: formData.income.schedule_c_is_sstb ? '#92400e' : '#0369a1'
                            }}>
                              {formData.income.schedule_c_is_sstb ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: '#dcfce7',
                              color: '#166534'
                            }}>
                              Yes
                            </span>
                          </td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(formData.income.schedule_c_net_income)}</td>
                        </tr>
                      )}
                      {formData.income.k1_entries.map((k1, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #d1fae5' }}>
                          <td style={{ padding: '8px 4px', fontWeight: '500' }}>{k1.business_name || `K-1 #${index + 1}`}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(k1.qbi_income)}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: k1.is_sstb ? '#fef3c7' : '#e0f2fe',
                              color: k1.is_sstb ? '#92400e' : '#0369a1'
                            }}>
                              {k1.is_sstb ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                            <span style={{
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: k1.subject_to_se_tax ? '#dcfce7' : '#f1f5f9',
                              color: k1.subject_to_se_tax ? '#166534' : '#64748b'
                            }}>
                              {k1.subject_to_se_tax ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', fontFamily: 'monospace' }}>{formatCurrency(k1.qbi_income)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#d1fae5' }}>
                        <td colSpan="4" style={{ padding: '10px 8px', fontWeight: '700', color: '#047857' }}>
                          Total QBI Deduction (20% of eligible income)
                        </td>
                        <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: '700', fontFamily: 'monospace', color: '#047857' }}>
                          {formatCurrency(results.qbi_deduction)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  
                  {results.self_employment_tax > 0 && (
                    <div style={{ marginTop: '12px', padding: '10px', background: '#fef3c7', borderRadius: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: '#92400e', fontWeight: '600' }}>
                          Self-Employment Tax (15.3% on 92.35% of SE income)
                        </span>
                        <span style={{ fontSize: '13px', fontWeight: '700', fontFamily: 'monospace', color: '#92400e' }}>
                          {formatCurrency(results.self_employment_tax)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {(formData.income.schedule_c_is_sstb || formData.income.k1_entries.some(k1 => k1.is_sstb)) && results.qbi_deduction === 0 && (
                    <p style={{ marginTop: '10px', fontSize: '10px', color: '#b45309', fontStyle: 'italic' }}>
                      Note: SSTB income is not eligible for QBI deduction when taxable income exceeds the phase-out threshold 
                      (${formData.filing_status === 'married_joint' ? '494,600' : '247,300'} for {formData.filing_status === 'married_joint' ? 'MFJ' : 'Single'} filers).
                    </p>
                  )}
                </div>
              )}

              {/* Social Security Analysis */}
              {results.ss_benefits_received > 0 && (
                <div className="ss-section" style={{
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderLeft: '4px solid #f59e0b',
                  borderRadius: '0 8px 8px 0',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  pageBreakInside: 'avoid'
                }}>
                  <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#92400e', marginBottom: '12px' }}>Social Security Benefits Analysis</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                    <div>
                      <p style={{ fontSize: '10px', color: '#b45309', textTransform: 'uppercase' }}>Benefits Received</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(results.ss_benefits_received)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#b45309', textTransform: 'uppercase' }}>Combined Income</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>{formatCurrency(results.ss_combined_income)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#b45309', textTransform: 'uppercase' }}>Taxable Amount ({results.ss_taxable_percent}%)</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626' }}>{formatCurrency(results.ss_taxable_amount)}</p>
                    </div>
                    <div>
                      <p style={{ fontSize: '10px', color: '#b45309', textTransform: 'uppercase' }}>Tax-Free Portion</p>
                      <p style={{ fontSize: '16px', fontWeight: '600', color: '#059669' }}>{formatCurrency(results.ss_benefits_received - results.ss_taxable_amount)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* IRMAA Analysis */}
              {results.irmaa_applicable && (
                <div className="irmaa-section" style={{
                  background: '#faf5ff',
                  border: '1px solid #e9d5ff',
                  borderLeft: '4px solid #a855f7',
                  borderRadius: '0 8px 8px 0',
                  padding: '16px 20px',
                  marginBottom: '20px',
                  pageBreakInside: 'avoid'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <h4 style={{ fontSize: '12px', fontWeight: '700', color: '#7c3aed', marginBottom: '4px' }}>Medicare Premium Surcharge (IRMAA)</h4>
                      <p style={{ fontSize: '10px', color: '#6b7280' }}>Income-Related Monthly Adjustment Amount based on your MAGI</p>
                    </div>
                    <div style={{
                      background: results.irmaa_tier > 0 ? '#fee2e2' : '#dcfce7',
                      color: results.irmaa_tier > 0 ? '#991b1b' : '#166534',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      {results.irmaa_tier_label}
                      {results.annual_irmaa_cost > 0 && ` (+${formatCurrency(results.annual_irmaa_cost)}/yr)`}
                    </div>
                  </div>
                  
                  <p style={{ fontSize: '11px', color: '#475569', marginBottom: '12px' }}>
                    Your MAGI for IRMAA: <strong>{formatCurrency(results.magi_for_irmaa)}</strong>
                  </p>
                  
                  <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e9d5ff' }}>
                        <th style={{ padding: '8px 4px', textAlign: 'left', color: '#7c3aed', textTransform: 'uppercase', fontSize: '9px' }}>MAGI Range ({formData.filing_status === 'married_joint' ? 'MFJ' : 'Single'})</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', color: '#7c3aed', textTransform: 'uppercase', fontSize: '9px' }}>Part B (Mo.)</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', color: '#7c3aed', textTransform: 'uppercase', fontSize: '9px' }}>Part D (Mo.)</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right', color: '#7c3aed', textTransform: 'uppercase', fontSize: '9px' }}>Annual Extra Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {irmaaBrackets.map((bracket, idx) => (
                        <tr 
                          key={idx}
                          style={{ 
                            background: results.irmaa_tier === bracket.tier ? '#e9d5ff' : 'transparent',
                            fontWeight: results.irmaa_tier === bracket.tier ? '600' : '400'
                          }}
                        >
                          <td style={{ padding: '6px 4px', borderBottom: '1px solid #f3e8ff' }}>
                            {bracket.magi_min > 0 ? formatCurrency(bracket.magi_min) : "$0"} – {bracket.magi_max < 999999999 ? formatCurrency(bracket.magi_max) : "No Limit"}
                            {results.irmaa_tier === bracket.tier && <span style={{ marginLeft: '6px', color: '#7c3aed' }}>◄</span>}
                          </td>
                          <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #f3e8ff' }}>${bracket.part_b_premium.toFixed(0)}</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #f3e8ff' }}>${bracket.part_d_irmaa.toFixed(0)}</td>
                          <td style={{ padding: '6px 4px', textAlign: 'right', fontFamily: 'monospace', borderBottom: '1px solid #f3e8ff' }}>{bracket.annual_extra_cost > 0 ? formatCurrency(bracket.annual_extra_cost) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Tax Planning Opportunities */}
              {results.tax_savings_suggestions && results.tax_savings_suggestions.length > 0 && (
                <div className="opportunities-section" style={{
                  background: '#fff',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '20px',
                  marginBottom: '20px',
                  pageBreakInside: 'avoid'
                }}>
                  <h3 style={{ 
                    fontSize: '13px', 
                    fontWeight: '700', 
                    color: '#1e3a5f', 
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ width: '4px', height: '16px', background: '#f59e0b', borderRadius: '2px' }}></span>
                    Tax Planning Opportunities
                  </h3>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {results.tax_savings_suggestions.map((suggestion, idx) => (
                      <div 
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '14px',
                          padding: '14px',
                          background: suggestion.priority === 'high' ? '#fef3c7' : suggestion.priority === 'medium' ? '#e0f2fe' : '#f1f5f9',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${suggestion.priority === 'high' ? '#f59e0b' : suggestion.priority === 'medium' ? '#0ea5e9' : '#94a3b8'}`
                        }}
                      >
                        <div style={{
                          minWidth: '28px',
                          height: '28px',
                          background: suggestion.priority === 'high' ? '#f59e0b' : suggestion.priority === 'medium' ? '#0ea5e9' : '#94a3b8',
                          color: 'white',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: '700'
                        }}>
                          {idx + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>{suggestion.title}</span>
                            {suggestion.potential_savings > 0 && (
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 10px',
                                background: '#dcfce7',
                                color: '#166534',
                                borderRadius: '12px',
                                fontWeight: '600'
                              }}>
                                Est. Savings: {formatCurrency(suggestion.potential_savings)}
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: '11px', color: '#475569', lineHeight: '1.5' }}>{suggestion.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Professional Footer */}
              <div className="report-footer" style={{
                marginTop: '32px',
                paddingTop: '16px',
                borderTop: '2px solid #e2e8f0'
              }}>
                <div style={{ 
                  background: '#f8fafc', 
                  borderRadius: '8px', 
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <p style={{ fontSize: '9px', color: '#64748b', lineHeight: '1.6' }}>
                    <strong style={{ color: '#475569' }}>Important Disclaimer:</strong> This tax projection is provided for informational and planning purposes only. 
                    It is based on the information provided and current federal tax law as of the date of this report. Actual tax liability may vary 
                    based on changes in income, deductions, credits, or amendments to the tax code. This document does not constitute tax, legal, 
                    or financial advice. Please consult with a qualified tax professional before making any decisions based on this projection.
                  </p>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '9px',
                  color: '#94a3b8'
                }}>
                  <span>Prepared by Steele Martin Jones, CPAs</span>
                  <span>Report Generated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-2 justify-end mt-4 print:hidden">
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Close
            </Button>
            <Button 
              onClick={() => {
                const reportContent = document.getElementById('client-report');
                if (!reportContent) return;
                
                const reportClientName = clientName || formData.client_name || 'Client';
                const printWindow = window.open('', '_blank');
                printWindow.document.write(`
                  <html>
                    <head>
                      <title>2026 Tax Projection - ${reportClientName}</title>
                      <style>
                        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                        
                        * { box-sizing: border-box; margin: 0; padding: 0; }
                        
                        body { 
                          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; 
                          padding: 40px; 
                          max-width: 850px; 
                          margin: 0 auto; 
                          font-size: 12px;
                          line-height: 1.6;
                          color: #334155;
                          -webkit-print-color-adjust: exact !important;
                          print-color-adjust: exact !important;
                        }
                        
                        /* PAGE BREAK CONTROL - Critical for clean printing */
                        .report-header { page-break-after: avoid; margin-bottom: 28px !important; }
                        .client-info-card { page-break-inside: avoid; page-break-after: avoid; }
                        .computation-section { page-break-inside: avoid; }
                        .bracket-section { page-break-inside: avoid; }
                        .ss-section { page-break-inside: avoid; }
                        .irmaa-section { page-break-inside: avoid; }
                        .opportunities-section { page-break-inside: avoid; page-break-before: auto; }
                        .report-footer { page-break-inside: avoid; page-break-before: auto; }
                        
                        /* Report Header - Larger spacing */
                        .report-header {
                          padding-bottom: 20px !important;
                          border-bottom: 3px solid #1e3a5f !important;
                        }
                        .report-header img {
                          height: 52px !important;
                        }
                        
                        /* Client Info Card - Navy Gradient with larger text */
                        .client-info-card {
                          background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%) !important;
                          border-radius: 12px !important;
                          padding: 28px !important;
                          margin-bottom: 28px !important;
                          color: white !important;
                        }
                        .client-info-card p[style*="font-size: 22px"] {
                          font-size: 26px !important;
                        }
                        .client-info-card p[style*="font-size: 28px"] {
                          font-size: 32px !important;
                        }
                        .client-info-card p[style*="font-size: 18px"] {
                          font-size: 22px !important;
                        }
                        .client-info-card p[style*="font-size: 10px"],
                        .client-info-card p[style*="font-size: 11px"],
                        .client-info-card p[style*="font-size: 12px"] {
                          font-size: 12px !important;
                        }
                        
                        /* Computation Section - Larger with more padding */
                        .computation-section {
                          background: #f8fafc !important;
                          border-radius: 10px !important;
                          padding: 28px !important;
                          margin-bottom: 28px !important;
                        }
                        .computation-section h3 {
                          font-size: 15px !important;
                          font-weight: 700 !important;
                          color: #1e3a5f !important;
                          margin-bottom: 20px !important;
                        }
                        .computation-section table {
                          width: 100% !important;
                          font-size: 13px !important;
                        }
                        .computation-section td {
                          padding: 12px 0 !important;
                        }
                        .computation-section td[style*="padding: 10px"] {
                          padding: 14px 10px !important;
                        }
                        
                        /* Bracket Section - Larger */
                        .bracket-section {
                          background: linear-gradient(90deg, #f0fdfa 0%, #ecfeff 100%) !important;
                          border: 1px solid #99f6e4 !important;
                          border-left: 4px solid #14b8a6 !important;
                          border-radius: 0 8px 8px 0 !important;
                          padding: 22px 24px !important;
                          margin-bottom: 28px !important;
                        }
                        .bracket-section p[style*="font-size: 12px"] {
                          font-size: 14px !important;
                        }
                        .bracket-section p[style*="font-size: 11px"] {
                          font-size: 13px !important;
                        }
                        .bracket-section div[style*="width: 40px"] {
                          width: 48px !important;
                          height: 48px !important;
                          font-size: 18px !important;
                        }
                        
                        /* Social Security Section - Larger */
                        .ss-section {
                          background: #fffbeb !important;
                          border: 1px solid #fde68a !important;
                          border-left: 4px solid #f59e0b !important;
                          border-radius: 0 8px 8px 0 !important;
                          padding: 22px 24px !important;
                          margin-bottom: 28px !important;
                        }
                        .ss-section h4 {
                          font-size: 14px !important;
                        }
                        .ss-section p[style*="font-size: 16px"] {
                          font-size: 18px !important;
                        }
                        .ss-section p[style*="font-size: 10px"] {
                          font-size: 12px !important;
                        }
                        
                        /* IRMAA Section */
                        .irmaa-section {
                          background: #faf5ff !important;
                          border: 1px solid #e9d5ff !important;
                          border-left: 4px solid #a855f7 !important;
                          border-radius: 0 8px 8px 0 !important;
                          padding: 22px 24px !important;
                          margin-bottom: 28px !important;
                        }
                        .irmaa-section h4 {
                          font-size: 14px !important;
                        }
                        .irmaa-section table {
                          width: 100% !important;
                          font-size: 11px !important;
                          border-collapse: collapse !important;
                        }
                        
                        /* Opportunities Section */
                        .opportunities-section {
                          background: #fff !important;
                          border: 2px solid #e2e8f0 !important;
                          border-radius: 10px !important;
                          padding: 24px !important;
                          margin-bottom: 24px !important;
                        }
                        .opportunities-section h3 {
                          font-size: 15px !important;
                          font-weight: 700 !important;
                          color: #1e3a5f !important;
                          margin-bottom: 20px !important;
                        }
                        .opportunities-section > div > div {
                          padding: 16px !important;
                          margin-bottom: 12px !important;
                        }
                        .opportunities-section span[style*="font-size: 12px"] {
                          font-size: 14px !important;
                        }
                        .opportunities-section p[style*="font-size: 11px"] {
                          font-size: 13px !important;
                        }
                        
                        /* Report Footer */
                        .report-footer {
                          margin-top: 40px !important;
                          padding-top: 20px !important;
                          border-top: 2px solid #e2e8f0 !important;
                        }
                        .report-footer p[style*="font-size: 9px"] {
                          font-size: 10px !important;
                        }
                        
                        /* Print-specific enhancements */
                        @media print {
                          body { 
                            padding: 24px 32px !important; 
                          }
                          
                          /* Ensure colors print properly */
                          .client-info-card,
                          .computation-section,
                          .bracket-section,
                          .ss-section,
                          .irmaa-section,
                          .opportunities-section {
                            -webkit-print-color-adjust: exact !important;
                            print-color-adjust: exact !important;
                          }
                          
                          /* Reduce padding for print */
                          .client-info-card { padding: 20px !important; }
                          .computation-section { padding: 16px !important; }
                        }
                      </style>
                    </head>
                    <body>
                      ${reportContent.innerHTML}
                    </body>
                  </html>
                `);
                printWindow.document.close();
                setTimeout(() => printWindow.print(), 300);
              }}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="print-report-btn"
            >
              <Printer size={16} className="mr-2" />
              Print Report
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
